import React, { useEffect, useState } from 'react';
import Breadcrumb from '../components/Breadcrumb';
import PatientList from './PatientList';
import Modal from '../components/Modal';
import {
	fetchPractitionerOptions,
	fetchPatientManagementList,
	uploadPatientCsvData,
	deletePatientManagementRecords
} from '../util/apiUtils';

const PatientManagementHome = ({ userInfo }) => {
	const [practitionerOptions, setPractitionerOptions] = useState(null);
	const [selectedPractitioner, setSelectedPractitioner] = useState(null);
	const [resultsList, setResultsList] = useState(null);
	const [message, setMessage] = useState(<LoadingIcon />);
	const [adminGsdlNo, setAdminGsdlNo] = useState('');
	const [displayUploadModal, setDisplayUploadModal] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);
	const [login, setLogin] = useState(false);

	/**
	 * Fetch the list of practitioner options the user has access for.
	 */
	useEffect(() => {
		fetchPractitionerOptions()
			.then((json) => {
				// If there is only one practitioner option, set it as the selected practitioner
				if (json && json.length === 1) {
					setSelectedPractitioner(json[0].practitioner);
				}
				setPractitionerOptions(json);
			})
			.catch((e) => {
				setMessage(
					<ErrorMessage text="An unexpected error has occurred. Please try again or call us at 800-522-4762 for assistance." />
				);
			});
	}, []);

	/**
	 * When the selected practitioner changes, fetch the list of PatientManagement records for the practitioner.
	 */
	useEffect(() => {
		if (!practitionerOptions) {
			return;
		}
		setResultsList(null);
		setMessage(<LoadingIcon />);

		fetchPatientManagementList(selectedPractitioner && selectedPractitioner.gsdlNo)
			.then((json) => {
				setResultsList(json);

				// An empty list was returned. Show a message
				if (json && !json.length) {
					let practText = practitionerOptions.length > 1 ? ' for the selected practitioner' : '';
					setMessage(
						<div className="space-top">
							No patient data found{practText}. Upload a patient data CSV file to get started.
							<br />
							<UploadButton title="Upload Patients" />
						</div>
					);
				} else {
					setMessage(null);
				}
			})
			.catch((e) => {
				if (e.response && e.response.status === 401) {
					showLogin();
					return;
				} else {
					setMessage(<ErrorMessage text="An unexpected error occurred while loading patient details" />);
				}
			});
	}, [practitionerOptions, selectedPractitioner]);

	/**
	 * Set the selected file whenever a new file is chosen.
	 * If there is a selected practitioner, upload the file for that practitioner.
	 * If there is no selected practitioner, display a Modal, asking which practitioner to use.
	 */
	const onFileChangeHandler = (e) => {
		e.preventDefault();
		// Store the file in case we need it after showing the practitioner selection modal
		setSelectedFile(e.target.files[0]);
		if (!selectedPractitioner) {
			setDisplayUploadModal(true);
		} else {
			uploadFile(e.target.files[0]);
		}
	};

	/**
	 * Upload a file to the server and put the results into the resultList state object.
	 * An array of uploaded PatientManagement records is expected as the result.
	 */
	const uploadFile = (file) => {
		if (!file) {
			return;
		}

		setMessage(
			<div className="space-top text-center">
				<UploadingMessage fileName={file.name} />
			</div>
		);

		uploadPatientCsvData(file, selectedPractitioner.gsdlNo)
			.then((json) => {
				if (json && json.length) {
					/* Filter the current list to strip out any items that were just re-uploaded and then return a new
					 * list with the uploaded items first. */
					setResultsList((prevResultsList) => {
						let oldList = prevResultsList || [];
						return [...json, ...oldList.filter((item) => !json.some((newItem) => newItem.id === item.id))];
					});
					let newMessage = `${json.length} patient${json.length > 1 ? 's have' : ' has'} been uploaded.`;
					setMessage(<SuccessMessage text={newMessage} />);
				} else {
					setMessage(
						<div className="space-top">
							No patient data was found in <span className="code">{file.name}</span>.
							<br />
							<UploadButton title="Upload Patients" />
						</div>
					);
				}
			})
			.catch((err) => {
				if (err.response && err.response.status === 401) {
					showLogin();
					return;
				} else {
					setMessage(
						<ErrorMessage text="An unexpected error has occurred. Please try again or call us at 800-522-4762 for assistance." />
					);
				}
			});
	};

	/**
	 * Send the selected records to the back-end for deletion and remove the selected records from the patient list.
	 * The back-end needs the GSDLNO of the records being deleted. Since office accounts can delete records for
	 *  multiple practitioners, we need to send the GSDLNO(s) with the delete request. To do that, we build a map of
	 *  key={GSDLNO}, value={List of Ids to delete for that practitioner}.
	 *
	 * @param toDelete An array of IDs to delete
	 * @return A Promise that is resolved or rejected after the delete has finished
	 */
	const handleDelete = (toDelete) => {
		if (!toDelete || !toDelete.length) {
			return;
		}

		// Filter the result list and build a map of gsdlNo/[IDs] to delete
		const mapToDelete = resultsList
			.filter((result) => toDelete.includes(result.id))
			.reduce((map, result) => {
				let newList = map[result.gsdlNo];
				if (!newList) {
					newList = [];
				}
				newList.push(result.id);

				return { ...map, [result.gsdlNo]: [...newList] };
			}, {});

		deletePatientManagementRecords(mapToDelete)
			.then(() => {
				let resultListAfterDelete = resultsList.filter((result) => !toDelete.includes(result.id));
				if (!resultListAfterDelete.length) {
					resultListAfterDelete = null;
				}

				setResultsList(resultListAfterDelete);

				let newMessage;

				if (!resultListAfterDelete || !resultListAfterDelete.length) {
					newMessage = (
						<>
							<span>All patient data has been deleted.</span>
							<div>
								<UploadButton title="Upload New Patients" />
							</div>
						</>
					);
				} else {
					newMessage = `${toDelete.length} patient${toDelete.length > 1 ? 's have' : ' has'} been deleted.`;
				}

				setMessage(<SuccessMessage text={newMessage} />);
			})
			.catch((err) => {
				if (err.response && err.response.status === 401) {
					showLogin();
					return;
				} else {
					setMessage(<ErrorMessage text="An unexpected error occurred while deleting patient details." />);
				}
			});
	};

	/**
	 * Update state when the selected practitioner changes.
	 * The event value will be the new practitioner's gsdlNo. Use that to find the correct
	 *  practitioner object from the practitionerOptions list.
	 */
	const handlePractitionerSelection = (evt) => {
		const gsdlNo = evt.target.value;
		let newPractitioner = (
			practitionerOptions.find((option) => option.practitioner && option.practitioner.gsdlNo === gsdlNo) || {}
		).practitioner;

		setSelectedPractitioner(newPractitioner);
	};

	/**
	 * When an admin user submits a gsdlNo, update the selected practitioner.
	 * The submitted gsdlNo probably does not match any practitioners in the options list. If that is the case,
	 *  create a new practitioner object (gsdlNo is the only attribute needed) and set that as the selected
	 *  practitioner.
	 */
	const handleAdminPractitionerSelection = (evt) => {
		evt.preventDefault();

		let gsdlNo = (adminGsdlNo || '').toUpperCase();

		let newPractitioner = (
			practitionerOptions.find((option) => option.practitioner && option.practitioner.gsdlNo === gsdlNo) || {}
		).practitioner;

		if (!newPractitioner) {
			newPractitioner = {
				gsdlNo: gsdlNo
			};
		}
		setSelectedPractitioner(newPractitioner);
	};

	/**
	 * Hide any open modals and show a login message.
	 */
	const showLogin = () => {
		window.$(".modal").modal("hide");
		setDisplayUploadModal(false);
		setLogin(true);
	}

	/**
	 * The file upload button component.
	 * The hidden <input> is automatically clicked when it's parent label is clicked.
	 * @param title - The text to display on the button
	 */
	const UploadButton = ({ title }) => (
		<div style={{ display: 'inline-block' }}>
			<label className="btn btn-primary btn-sm radius" style={{ marginBottom: '10px' }}>
				{title}
				<input
					type="file"
					style={{ display: 'none' }}
					name="file"
					onChange={onFileChangeHandler}
					accept=".txt,.csv"
				/>
			</label>
		</div>
	);

	/**
	 * The message to show when an upload is in progress
	 */
	const UploadingMessage = ({ fileName }) => (
		<h3 className="text-center">
			Extracting patient details from {fileName}.
			<LoadingIcon />
		</h3>
	);

	const ErrorMessage = ({ text }) => (
		<div className="space-top">
			<span className="text-danger">
				<i className="fa fa-exclamation-circle"></i> {text}
			</span>
		</div>
	);

	const SuccessMessage = ({ text }) => (
		<div className="space-top">
			<span className="text-success">
				<i className="fa fa-check-circle"></i> {text}
			</span>
		</div>
	);

	const isAdminUser = userInfo && userInfo.adminUser === 'true';

	if (login) {
		const redirect = encodeURIComponent(window.location.href);
		return (
			<div>
				<Breadcrumb breadcrumbStep="Patient Management" singleLevel={true} />
				<div className="container space-bottom">
					<div>
						<h1>Patient Management</h1>
						<div className="alert alert-info">
							<p>You are not logged in or your session has expired.</p>
						</div>
						<p>Please log in to continue.</p>
						<ul>
							<li>
								<a href={`/mygdx/login?url=${redirect}`}>Practitioner login</a>
							</li>
						</ul>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div>
			<Breadcrumb breadcrumbStep="Patient Management" singleLevel={true} />
			<div className="container space-bottom">
				<div>
					<h1>Patient Management</h1>
					<div className="row">
						<div className="col-sm-7">
							<p>
								Don't want to enter patient and insurance information anymore? Export patient and insurance
								information from your management system and upload to Genova's. You will never need to enter
								patient and insurance information again.
							</p>
							<p>
								Upload a CSV file containing your patient details. The saved details will be included in patient
								searches when placing new orders, thus saving you time.
								<br />
								<a
									href={`${process.env.PUBLIC_URL}/patient_sample.csv?t=${cacheString}`}
									download
									className="small"
								>
									<span className="fa fa-download"></span> Download Sample CSV File
								</a>
							</p>
						</div>
						<div className="col-sm-5 col-xs-8">
							<div className="embed-responsive embed-responsive-16by9">
								<iframe title="YouTube video player" src="https://www.youtube.com/embed/mHu2rjvna64" allow="accelerometer; clipboard-write; encrypted-media; gyroscope;" allowFullScreen="yes"></iframe>
							</div>
						</div>
					</div>
				</div>
				<div className="row">
					<div className="col-sm-6">
						{practitionerOptions && practitionerOptions.length > 1 && (
							<>
								<h4>Patients For:</h4>
								<select
									className="form-control"
									id="pract-select"
									onChange={handlePractitionerSelection}
									value={(selectedPractitioner && selectedPractitioner.gsdlNo) || ''}
								>
									{/*Need a blank option to "select" if an admin user types in a GSDLNO to search*/}
									{isAdminUser && <option style={{ color: 'gray' }}>Select a practitioner</option>}
									<option>All Accounts</option>
									{practitionerOptions.map(function (option) {
										return (
											<option key={option.practitioner.gsdlNo} value={option.practitioner.gsdlNo}>
												{option.practitioner.firstName} {option.practitioner.lastName},{' '}
												{option.practitioner.degree} ({option.practitioner.gsdlNo})
											</option>
										);
									})}
								</select>
							</>
						)}
						{isAdminUser && (
							<form
								autoComplete="off"
								className="form-inline space-top"
								style={{ margin: '0px' }}
								onSubmit={handleAdminPractitionerSelection}
							>
								<span style={{ fontSize: '16px', fontWeight: 'bold' }}>Admin: </span>
								<div className="input-group" style={{ width: '140px' }}>
									<input
										type="text"
										className="form-control"
										maxLength="5"
										placeholder="GSDLNO"
										style={{ textTransform: 'uppercase' }}
										value={adminGsdlNo}
										onChange={(evt) => setAdminGsdlNo(evt.target.value)}
									/>
									<span className="input-group-btn">
										<button
											className="btn btn-default"
											type="submit"
											style={{ minWidth: '0px', margin: '0px', padding: '9px 12px' }}
										>
											<i className="fa fa-w fa-search"></i>
										</button>
									</span>
								</div>
							</form>
						)}
					</div>
				</div>
				{message}
				{resultsList && !!resultsList.length && (
					<>
						<div className="row">
							<div className="col-xs-12">
								<div className="pull-right">
									<UploadButton title="Upload New patients" />
								</div>
							</div>
						</div>
						<div className="panel panel-default">
							<PatientList
								patientList={resultsList}
								onDelete={handleDelete}
								displayGdxId={isAdminUser || (practitionerOptions && practitionerOptions.length > 1)}
							/>
						</div>
					</>
				)}
			</div>
			{displayUploadModal && (
				<Modal
					title="Patient Management Upload"
					confirmAction="Upload"
					cancelAction="Cancel"
					onSave={() => uploadFile(selectedFile)}
					disableConfirm={!selectedPractitioner}
					modalHidden={() => setDisplayUploadModal(false)}
				>
					<div>
						<h4>Select a Practitioner</h4>
						<select
							className="form-control"
							id="upload-pract-select"
							onChange={handlePractitionerSelection}
						>
							<option>Select a Practitioner</option>
							{practitionerOptions.map(function (option) {
								return (
									<option key={option.practitioner.gsdlNo} value={option.practitioner.gsdlNo}>
										{option.practitioner.firstName} {option.practitioner.lastName},{' '}
										{option.practitioner.degree} ({option.practitioner.gsdlNo})
									</option>
								);
							})}
						</select>
					</div>
				</Modal>
			)}
		</div>
	);
};

/**
 * A component that displays a spinner icon
 */
const LoadingIcon = () => (
	<div className="space-top text-center">
		<i className="fa fa-spinner fa-spin fa-fw " style={{ fontSize: '3em' }}></i>
	</div>
);

// Add a query string to the sample CSV file link so updates to the file on the server will be picked up
const cacheString = new Date().getTime();

export default PatientManagementHome;
